import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { extractApiError } from '../../../lib/api';
import {
  getByEducatorToken,
  startEducationByToken,
  completeByEducatorToken,
  updateTbmSessionRecordsByToken,
  upsertTbmSessionRecordsByToken,
  addEducationTextByToken,
  updateEducationTextByToken,
  uploadEducationPhotoByToken,
  deleteEducationEntryByToken,
  getEducationAnnotationByToken,
  saveEducationAnnotationByToken,
  reorderEducationEntriesByToken,
  EducatorTokenResult,
} from '../../../lib/tbm';
import { TbmEducateView } from '../../../components/tbm/TbmEducateView';
import type { TbmEducateAdapter } from '../../../components/tbm/tbmEducateAdapter';
import { LocaleProvider } from '../../../lib/i18n/useLocale';
import { Card } from '@safetyhub/ui';

// 교육담당자 토큰 페이지 — direct(교육 진행) 토큰 하나만 다룬다. delegate 토큰의 위임 작성·진행자
// 지정 단계를 두지 않는다 — 서버에도 그 발급 경로·엔드포인트가 없고, COMPOSED TBM의 위임 작성은
// `createDelegatedComposedReport`(`lib/reports`) + `/external/composed/:token` 화면이 맡는다.

export default function EducatorPage() {
  return (
    <LocaleProvider>
      <EducatorPageInner />
    </LocaleProvider>
  );
}

function EducatorPageInner() {
  const { token } = useParams<{ token: string }>();

  const [data, setData] = useState<EducatorTokenResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<{ code: string; message: string; details?: Record<string, unknown> } | null>(null);

  const loadData = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await getByEducatorToken(token, new URLSearchParams(window.location.search).get('via'));
      setData(result);
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) loadData();
  }, [token, loadData]);

  /**
   * 로딩 UI 없이 데이터만 조용히 갱신 (교육 기록 편집 시 스크롤 유지용).
   *
   * ⚠️ identity를 token에 고정하는 것이 **기능 요건**이다 — `TbmEducateView`의 라이브 폴링이
   * 이 함수를 effect 의존성으로 쓴다(`[isMultiSession, canEditRecords, onRefresh]`). 매 렌더
   * 새 함수를 주면 폴링이 setData → 리렌더 → 새 identity → effect 재실행으로 5초 타이머를
   * 매번 처음부터 다시 깔아, 같은 주기인 서명 현황 폴링이 발화 직전에 계속 리셋된다.
   */
  const refreshData = useCallback(async () => {
    if (!token) return;
    try {
      const result = await getByEducatorToken(token, new URLSearchParams(window.location.search).get('via'));
      setData(result);
    } catch {
      // 조용히 무시
    }
  }, [token]);

  /**
   * 토큰 기반 어댑터 — DirectEducatorView가 호출하던 모든 *ByToken 함수를 그대로 감싼다.
   *
   * ⚠️ refreshData와 같은 이유로 **useMemo가 필수**다. `TbmEducateView`의 서명 현황 폴링이
   * `pollAttendees`(deps `[adapter]`) → effect deps로 이 객체를 타고 들어간다.
   * 조기 반환 뒤에서는 훅을 쓸 수 없어 여기(모든 반환 위)에서 만든다.
   */
  const directContactName = data?.token.contactName ?? null;
  const tokenAdapter = useMemo<TbmEducateAdapter | null>(() => {
    if (!token || directContactName === null) return null;
    return {
      educatorName: directContactName,
      refreshTbm: async () => {
        const via = new URLSearchParams(window.location.search).get('via');
        const result = await getByEducatorToken(token, via);
        return result.tbm;
      },
      startEducation: () => startEducationByToken(token),
      complete: (dto) => completeByEducatorToken(token, dto),
      updateSessionRecords: (sessionId, records) => updateTbmSessionRecordsByToken(token, sessionId, records),
      upsertSessionRecords: (records) => upsertTbmSessionRecordsByToken(token, records),
      addText: (content) => addEducationTextByToken(token, content),
      uploadPhoto: (file) => uploadEducationPhotoByToken(token, file),
      deleteEntry: (entryId) => deleteEducationEntryByToken(token, entryId),
      updateText: (entryId, content) => updateEducationTextByToken(token, entryId, content),
      reorder: (entries) => reorderEducationEntriesByToken(token, entries),
      getAnnotation: (entryId) => getEducationAnnotationByToken(token, entryId),
      saveAnnotation: (entryId, annotationData) => saveEducationAnnotationByToken(token, entryId, annotationData),
      annotationCacheKey: `educationAnnotation-${token}`,
    };
  }, [token, directContactName]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-surface-soft flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  if (error) {
    const isExpired = error.code === 'TBM_EDUCATION_PERIOD_EXPIRED';
    // 비활성화된 링크 = 교육이 이미 완료된 경우
    const isCompleted = !isExpired && error.message.includes('비활성화');

    const expiredPeriod: string | null = isExpired
      ? (error.details?.period as string | null) ?? null
      : null;

    return (
      <div className="min-h-screen bg-surface-soft flex items-center justify-center p-4">
        <Card
          radius="lg"
          padding="xl"
          elevation="md"
          className="max-w-md w-full text-center"
        >
          {isExpired ? (
            <>
              <div className="h-16 w-16 mx-auto rounded-full bg-amber-100 flex items-center justify-center">
                <svg className="h-8 w-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="mt-4 text-lg font-bold text-ink-900">교육 기간이 만료되었습니다</h2>
              {expiredPeriod && (
                <p className="mt-2 text-sm text-ink-600">
                  교육 기간: {expiredPeriod}
                </p>
              )}
              <p className="mt-2 text-sm text-ink-500">
                교육 기간이 종료되어 더 이상 접근할 수 없습니다.
              </p>
              <button
                onClick={() => window.history.back()}
                className="mt-4 text-sm text-brand-600 hover:underline"
              >
                돌아가기
              </button>
            </>
          ) : isCompleted ? (
            <>
              <div className="h-16 w-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="mt-4 text-lg font-bold text-ink-900">교육이 완료되었습니다</h2>
              <p className="mt-2 text-sm text-ink-600">
                이 TBM 교육은 이미 완료 처리되었습니다.<br />
                이 페이지를 닫으셔도 됩니다.
              </p>
            </>
          ) : (
            <>
              <div className="h-16 w-16 mx-auto rounded-full bg-red-100 flex items-center justify-center">
                <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="mt-4 text-lg font-bold text-ink-900">접근할 수 없습니다</h2>
              <p className="mt-2 text-sm text-ink-600">{error.message}</p>
              <p className="mt-3 text-xs text-ink-400">
                링크가 만료되었거나 유효하지 않을 수 있습니다.<br />
                안전관리자에게 문의해 주세요.
              </p>
            </>
          )}
        </Card>
      </div>
    );
  }

  if (!data || !token || !tokenAdapter) return null;

  return <TbmEducateView tbm={data.tbm} adapter={tokenAdapter} onReload={loadData} onRefresh={refreshData} />;
}
